import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { people, horses, tests } from "../local/db";

// #642 — the join tables behind the Order of Go.
//
// A score-ride row carries riderId / horseId / testId and NOTHING else: reconcile
// never denormalizes a name onto it. So every string the announcer reads out is
// resolved from these caches, all of which prefetchDay already filled at setup.
//
// people + horses have no per-show filter on their routes (same as scribe-fe's
// hydrate()) — they're org-wide and small enough to hold in full.

export function usePeople(online) {
  const api = useScoreApi();
  return useCachedResource(
    async () => {
      const cached = await people.getAll();
      return cached.length ? cached : undefined;
    },
    async () => {
      const list = unwrap(await api.people.listPeople());
      await people.putAll(list ?? []);
      return list ?? [];
    },
    [api],
    online,
  );
}

export function useHorses(online) {
  const api = useScoreApi();
  return useCachedResource(
    async () => {
      const cached = await horses.getAll();
      return cached.length ? cached : undefined;
    },
    async () => {
      const list = unwrap(await api.horses.listHorses());
      await horses.putAll(list ?? []);
      return list ?? [];
    },
    [api],
    online,
  );
}

// Test is a per-RIDE property (#435 — class.testId is only a hint), and there's
// no bulk test route, so fetch exactly the tests this ring's rides reference and
// only the ones that aren't cached yet.
export function useTestsForRides(testIds, online) {
  const api = useScoreApi();
  const idsKey = [...new Set((testIds || []).filter(Boolean))].sort().join(",");

  return useCachedResource(
    async () => {
      if (!idsKey) return [];
      const wanted = new Set(idsKey.split(","));
      const cached = (await tests.getAll()).filter((t) => wanted.has(t.id));
      return cached.length ? cached : undefined;
    },
    async () => {
      if (!idsKey) return [];
      const wanted = idsKey.split(",");
      const cached = await tests.getAll();
      const have = new Map(cached.map((t) => [t.id, t]));
      const missing = wanted.filter((id) => !have.has(id));

      const fetched = await Promise.all(
        missing.map((id) =>
          api.tests
            .getTest(id)
            .then(unwrap)
            .catch(() => null),
        ),
      );
      const fresh = fetched.filter(Boolean);
      await tests.putAll(fresh);
      for (const t of fresh) have.set(t.id, t);

      return wanted.map((id) => have.get(id)).filter(Boolean);
    },
    [api, idsKey],
    online,
  );
}
